import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check, X } from "lucide-react";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4 py-16">
    <div className="absolute inset-0 radial-violet pointer-events-none" />
    <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 z-10">
      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
        <span className="font-serif font-bold text-white text-sm">g</span>
      </div>
      <span className="font-serif font-bold text-lg text-white">grime.bio</span>
    </Link>
    <div className="relative w-full max-w-sm animate-fade-up">
      <div className="rounded-2xl border border-[#22222B] bg-[#111115] p-8 shadow-2xl">
        <h1 className="font-serif font-bold text-3xl text-white text-center">{title}</h1>
        <p className="text-sm text-zinc-500 text-center mt-2 mb-7">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function Register() {
  const [params] = useSearchParams();
  const [username, setUsername] = useState(params.get("u") || "");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [uStatus, setUStatus] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!username) { setUStatus(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/check-username/${encodeURIComponent(username)}`);
        setUStatus(data);
      } catch { setUStatus(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { username, password, invite_code: inviteCode });
      login(data.token, data.user);
      toast.success("Account created! Welcome to grime.bio 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="You need a valid invite code to join">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Username</label>
          <div className="relative flex items-center bg-[#18181F] border border-[#22222B] focus-within:border-violet-500/60 rounded-xl px-4 py-2.5 transition-colors">
            <span className="font-mono text-sm text-zinc-600 select-none">grime.bio/</span>
            <input
              data-testid="register-username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ""))}
              placeholder="u"
              maxLength={30}
              className="flex-1 bg-transparent outline-none text-white text-sm min-w-0 placeholder:text-zinc-600"
            />
            {username && uStatus && (uStatus.available ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-rose-400" />)}
          </div>
          <p className="text-[11px] text-zinc-600">Single character usernames allowed (e.g. grime.bio/x).</p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Password</label>
          <input
            type="password"
            data-testid="register-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="min 6 characters"
            autoComplete="new-password"
            className="w-full bg-[#18181F] border border-[#22222B] focus:border-violet-500/60 rounded-xl px-4 py-2.5 text-white text-sm outline-none transition-colors placeholder:text-zinc-600"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Invite Code</label>
          <input
            data-testid="register-invite"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="XXXXXXXX"
            className="w-full bg-[#18181F] border border-[#22222B] focus:border-violet-500/60 rounded-xl px-4 py-2.5 text-white text-sm font-mono tracking-widest outline-none transition-colors placeholder:text-zinc-600"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white font-medium rounded-xl py-2.5 transition-colors shadow-[0_0_20px_rgba(139,92,246,0.3)]"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>
      </form>
      <p className="text-center text-sm text-zinc-500 mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-violet-400 hover:text-violet-300" data-testid="to-login-link">Log in</Link>
      </p>
    </AuthShell>
  );
}
